import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions, DialogContent, DialogTitle, styled, Typography } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { ResponsiveDialog, UnsavedDialog, useSelection, useSettings, useUserData, Utils } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

import QuantitySelectCell from './QuantitySelectCell';

interface OrdersLinesAddMerchDialogProps {
	open: boolean;
	closeDialog: VoidFunction;
	orderUid: string;
	pricingGroupId?: number;
	existingRows?: any[];
	totalPointsEarned?: number;
	isEdit?: boolean;
	updateRefresh?: VoidFunction;
}

const StyledViewTable = styled(ViewTable)(({ theme }) => ({
	'.MuiDataGrid-cell.OrdersLinesAddTable-quantityCell': {
		padding: 0
	}
}));

export default function OrdersLinesAddMerchDialog(props: OrdersLinesAddMerchDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const onDialogClose = () => document.dispatchEvent(new Event('onOrdersLinesAddMerchDialogClose'));

	const [hasModelChanged, setHasModelChanged] = React.useState(false);

	const defaultSelectedItems = React.useMemo(
		() =>
			props.existingRows?.map(row => ({
				orme_quantity: row.orme_quantity,
				item_uid: row.orme_item_uid,
				itmp_points_price: row.orme_points_price,
				itmp_tot_points_price: row.orme_tot_points_price
			})) || [],
		[props.existingRows]
	);

	const parseSelectedItems = React.useCallback((newItems: any[]) => {
		setHasModelChanged(true);
		return newItems.map(item => {
			const newQuantity = item.quantity || item.itmp_ords_multiple || 1;
			const totPointsPrice = item.itmp_points_price * newQuantity;
			return { ...item, orme_quantity: newQuantity, itmp_tot_points_price: totPointsPrice };
		});
	}, []);

	const { selectedItems, setSelectedItems } = useSelection({
		module: 'items',
		isSelecting: true,
		defaultSelectedItems,
		parseSelectedItems
	});

	const rowsParser = (rows: any[]) =>
		rows.map(row => {
			const selectedItem = selectedItems.find(item => item.item_uid === row.itmp_item_uid) || {};
			return { ...row, ...selectedItem };
		});

	const handleQuantityChange = (item: any, newQuantity?: number) => {
		setHasModelChanged(true);
		setSelectedItems([
			...selectedItems.filter(prevItem => prevItem.item_uid !== item.item_uid),
			{ ...item, orme_quantity: newQuantity, itmp_tot_points_price: item.itmp_points_price * (newQuantity || 0) }
		]);
	};

	function save() {
		dispatch(Utils.alertProgressMsg(t));
		Utils.executeStored({
			action: 'st_orders_merchandise_add',
			dispatch,
			data: {
				orme_ords_uid: props.orderUid,
				orme_user_created_uid: userData.user_uid,
				merchandise: selectedItems.map(item => ({
					orme_item_uid: item.item_uid,
					orme_quantity: item.orme_quantity
				}))
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				props.closeDialog();
				props.updateRefresh?.();
			}
		});
	}

	const columns: GridColDef[] = [
		{
			field: 'item_erp_id',
			headerName: t('DETAILS_ORDERS_MERCH_FIELD_ERP_ID'),
			minWidth: 120
		},
		{
			field: 'item_title',
			headerName: t('DETAILS_ORDERS_MERCH_FIELD_ITEM'),
			minWidth: 400,
			flex: 4,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		},
		{
			field: 'itsr_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM_SERIES'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'itms_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM_MEASURES'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orme_quantity',
			headerName: t('DETAILS_ORDERS_MERCH_FIELD_QUANTITY'),
			minWidth: 100,
			cellClassName: 'OrdersLinesAddTable-quantityCell',
			renderCell: ({ value, row }) =>
				props.isEdit ? (
					<QuantitySelectCell
						quantity={value}
						multiples={row.item_ords_multiple}
						minQty={row.item_ords_qty_min}
						handleChange={newQuantity => handleQuantityChange(row, newQuantity)}
					/>
				) : (
					value
				),
			sortable: false,
			hideable: false
		},
		{
			field: 'itmp_points_price',
			type: 'number',
			headerName: t('DETAILS_ORDERS_MERCH_FIELD_UNIT_PRICE'),
			minWidth: 125,
			renderCell: ({ value }) => ViewTableUtils.renderCellWithTooltip({ value: -value }),
			hideable: false
		},
		{
			field: 'itmp_tot_points_price',
			type: 'number',
			headerName: t('DETAILS_ORDERS_MERCH_FIELD_TOT_TAX_EXCL'),
			minWidth: 125,
			renderCell: ({ value }) => ViewTableUtils.renderCellWithTooltip({ value: value ? -value : undefined }),
			sortable: false,
			hideable: false
		}
	];

	const totalPointsSpent = Utils.calcObjectsTotal(selectedItems, 'itmp_tot_points_price');

	const FooterCustomInfo = React.useCallback(() => {
		return (
			<Typography
				className="h-full p-6 font-600"
				variant="subtitle2"
				color={totalPointsSpent > (props.totalPointsEarned || 0) ? 'error.main' : 'inherit'}
			>
				<FormattedMessage
					id="merchandiseTable.footer"
					defaultMessage={t('DIALOG_ORDERS_MERCH_TABLE_FOOTER')}
					values={{ spent: totalPointsSpent || 0, earned: props.totalPointsEarned || 0 }}
				/>
			</Typography>
		);
	}, [props.totalPointsEarned, t, totalPointsSpent]);

	return (
		<ResponsiveDialog
			open={props.open}
			onClose={onDialogClose}
			maxWidth="xl"
			fullWidth
			slotProps={{ paper: { className: 'h-full' } }}
		>
			<UnsavedDialog
				hasModelChanged={hasModelChanged}
				eventName="onOrdersLinesAddMerchDialogClose"
				onClose={() => {
					setSelectedItems(defaultSelectedItems);
					props.closeDialog();
				}}
			/>
			<DialogTitle>{t('DIALOG_ORDERS_MERCH_ADD_TITLE')}</DialogTitle>
			<DialogContent className="pb-0">
				<StyledViewTable
					className="h-full"
					module="items"
					query={{ storedProcedure: 'st_items_merchpoints_list_active' }}
					columns={columns}
					defaultFilters={[
						{ filt_field: 'item_blocked', filt_clause: 'not_equal', filt_value: 1 },
						props.pricingGroupId
							? { filt_field: 'itpr_pric_id', filt_clause: 'equals', filt_value: props.pricingGroupId }
							: { filt_field: 'itpr_pric_id', filt_clause: 'is_null' }
					]}
					showExportButton={false}
					slots={{ footerCustomInfo: FooterCustomInfo }}
					rowsParser={rowsParser}
					selectionProps={props.isEdit ? { mode: 'multiple', selectedItems } : undefined}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onDialogClose}>{t('DIALOG_ACTION_CLOSE')}</Button>
				{props.isEdit && (
					<Button
						color="secondary"
						onClick={save}
						disabled={totalPointsSpent > (props.totalPointsEarned || 0)}
					>
						{t('DIALOG_ACTION_SAVE')}
					</Button>
				)}
			</DialogActions>
		</ResponsiveDialog>
	);
}
